import { ref, reactive, watch, onMounted, nextTick } from 'vue';
import { DEFAULT_POLLING_TIMEOUT_MS, TIMEOUT_500_MS, TIMEOUT_5000_MS } from '@base-shared/utils/constants';
import { useFileUploadStorage } from '@/composables/file-upload/use-file-upload-storage';
import { useFileUploadApi } from '@/composables/file-upload/use-file-upload-api';
import { useFileUploaderStore } from '@/pinia/stores/file-upload/fileUploaderStore';
import PollingManager from '@base-shared/utils/polling-manager';
import CONST from '@/globals/constants';
import type { FileUploadItem, FileUploadItemStatus } from '@base-shared/types/file-uploader';
import type { AxiosError } from 'axios';

/**
 * useFileUploaderPolling composable
 * Watches fileUploaderStore and polls all file uploads in the store in parallel.
 * Only one polling instance runs at a time.
 */

const POLLING_BATCH_SIZE = 5; // Number of file uploads to poll in parallel per batch
const POLL_ID = 'file-uploader-polling';

type UploadPollingItem = {
  uid: string;
  resourceId?: string;
  file: FileUploadItem;
  startTime: number;
};

export function useFileUploaderPolling (pollListener?: (data: any) => void) {
  const storage = useFileUploadStorage();
  const fileUploaderStore = useFileUploaderStore();
  const api = useFileUploadApi();

  const pollingActive = ref(false);
  const uploadPollingList = reactive<Array<UploadPollingItem>>([]);

  /**
   * Watch for changes in the file upload storage and construct
   * the polling list accordingly. If a file's status changes,
   * reset its polling timer.
   */
  watch(storage.files, (newVal) => {
    const newList: Array<UploadPollingItem> = [];
    if (newVal?.length) {
      newVal.forEach((item) => {
        const existing = uploadPollingList.find((p) => p.uid === item.uid || p.resourceId === item.resourceId);
        let existingStartTime = existing?.startTime;
        if (existing && item.status !== existing?.file.status) {
          // Reset polling timers if status has changed
          existingStartTime = undefined;
        }

        newList.push({
          uid: item.uid,
          resourceId: item.resourceId,
          file: item,
          startTime: existingStartTime ?? Date.now()
        });
      });
    }

    uploadPollingList.splice(0, uploadPollingList.length, ...newList);
  }, {
    deep: true,
    immediate: true
  });

  // ──── Helpers ─────────────────────────────────────────

  /**
   * Fetches the status of a file upload item from the API
   * and handles the response. If the file is not found,
   * it removes it from local storage.
   * @param file - The file upload item to fetch the status for.
   */
  async function fetchStatusFromFileUploadItem (file: FileUploadItem) {
    const resourceId = file?._edit?.resourceId ??
      file?._edit?.uploadRequests?.createMediaInstanceResponseData?.resourceId ??
      file?._edit?.getMediaFullDetails?.media?.resourceId;
    const { response, error } = await api.fetchStatusFromId(resourceId ?? '');

    const errorStatus = (error as AxiosError)?.response?.status ?? 'unknown';
    // If file is not found, remove it from local storage and update the store.
    if (errorStatus === 404) {
      // Propagate the failed status to the file to reflect
      // in ui before removing it from storage.
      storage.upsertFile({
        ...file,
        status: CONST.FILE_UPLOAD_ITEM_STATUS.FAILED as FileUploadItemStatus,
      });

      // Delay the removal slightly to ensure the UI
      nextTick(() => {
        storage.removeFile(file);
        fileUploaderStore.setUploadedFiles({ prevAction: 'deletedFile', files: storage.getFiles(), deletedFile: file });
      });
    }

    if (pollListener) pollListener(response);
  }

  /**
   * Construct an array of functions that return promises
   * for polling the status of each file upload item.
   */
  function getUploadPromiseFns (): Array<() => Promise<void>> {
    if (!uploadPollingList?.length) return [];
    const fns: Array<() => Promise<void>> = [];

    uploadPollingList.forEach((item) => {
      // Only poll if the upload is still pending and within the timeout window
      const elapsed = Date.now() - item.startTime;
      if (elapsed < DEFAULT_POLLING_TIMEOUT_MS) {
        fns.push(() => fetchStatusFromFileUploadItem(item.file));
      }
    });

    return fns;
  }

  /**
   * Group the polling functions into batches and execute them sequentially.
   * @param fns - Array of functions that return promises for polling.
   * @param batchSize - Number of functions to execute in parallel per batch.
   */
  async function runInBatches (fns: Array<() => Promise<void>>, batchSize: number) {
    for (let i = 0; i < fns.length; i += batchSize) {
      const batchFns = fns.slice(i, i + batchSize);
      await Promise.allSettled(batchFns.map((fn) => fn()));
      await new Promise((resolve) => setTimeout(resolve, TIMEOUT_500_MS));
    }
  }

  /**
   * Main polling function that retrieves the upload promise
   * functions and executes them in batches. After polling,
   * it refreshes the file list from storage.
   */
  async function pollFn () {
    const fns = getUploadPromiseFns();
    if (fns.length) {
      await runInBatches(fns, POLLING_BATCH_SIZE);
    }

    storage.getFiles();
  }

  // ──── Public API ─────────────────────────────────────────

  function startPolling () {
    if (pollingActive.value) return;
    pollingActive.value = true;

    if (!PollingManager.isRunning(POLL_ID)) {
      PollingManager.register(POLL_ID, pollFn, TIMEOUT_5000_MS / 1000);
      PollingManager.start(POLL_ID);
    }
  }

  function stopPolling () {
    pollingActive.value = false;

    if (PollingManager.isRunning(POLL_ID)) {
      PollingManager.stop(POLL_ID);
      PollingManager.deregister(POLL_ID);
    }
  }

  onMounted(() => {
    // Make sure to sync latest data from storage to store.
    storage.getFiles();
  });

  return {
    startPolling,
    stopPolling,
    pollingActive
  };
}
